<template>
  <div>
    <div class="row mb-3">
      <div class="form-group col-md-3">
        <label for="centrals">{{ $t("Centrals") }}:</label>
        <v-select
            :options="centerOptions"
            :reduce="item => item.id"
            label="name"
            v-model="filters.center_id"
            class="vselect"
            @input="handleCenterChange"
            :loading="loadingStates.centers"
        />
      </div>

      <div class="form-group col-md-3">
        <label for="unit">{{ $t("Unit") }}:</label>
        <v-select
            :options="unitOptions"
            :reduce="item => item.id"
            label="name"
            v-model="filters.unit_id"
            class="vselect"
            :disabled="!filters.center_id"
            @input="handleUnitChange"
            :loading="loadingStates.units"
        />
      </div>

      <div class="form-group col-md-3">
        <label for="vehicleType">{{ $t("Vehicle type") }}:</label>
        <v-select
            :options="vehicleTypeOptions"
            :reduce="item => item.code"
            label="name"
            v-model="filters.vehicle_type_code"
            class="vselect"
            @input="handleVehicleTypeChange"
            :loading="loadingStates.vehicle_types"
        />
      </div>

      <div class="form-group col-md-3">
        <label for="filter">{{ $t("Vehicle") }}:</label>
        <v-select
            :options="vehicleOptions"
            :reduce="item => item.id"
            label="name"
            v-model="filters.vehicle_id"
            class="vselect"
            @input="handleVehicleChange"
            :loading="loadingStates.vehicles"
        />
      </div>

      <div class="form-group col-md-6 position-relative" id="address-container">
        <label for="address">{{ $t("Address") }}:</label>
        <div class="custom-address-input" id="address-input-wrapper">
          <div class="address-input-container">
            <i class="fas fa-map-marker-alt address-icon"></i>
            <input
                ref="addressInput"
                type="text"
                id="address"
                v-model="filters.address"
                class="address-input"
                :placeholder="$t('Start typing an address...')"
                autocomplete="off"
                @input="handleAddressInput"
                @focus="handleAddressFocus"
                @blur="handleAddressBlur"
                @keydown="handleAddressKeydown"
            />
            <button
                v-if="filters.address"
                type="button"
                class="clear-address-btn"
                @click="clearAddress"
                tabindex="-1"
            >
              <i class="fas fa-times"></i>
            </button>
            <div v-if="loadingStates.geocoding" class="address-spinner">
              <i class="fas fa-spinner fa-spin"></i>
            </div>
          </div>

          <div
              v-if="showAddressDropdown && addressSuggestions.length > 0"
              class="address-dropdown"
              ref="addressDropdown"
          >
            <div
                v-for="(suggestion, index) in addressSuggestions"
                :key="index"
                class="address-option"
                :class="{ 'active': selectedSuggestionIndex === index }"
                @mousedown.prevent="selectAddressSuggestion(suggestion)"
                @mouseenter="selectedSuggestionIndex = index"
            >
              <div class="address-main">
                <i class="fas fa-map-marker-alt text-primary mr-2"></i>
                <span v-html="highlightMatch(suggestion.main_text, filters.address)"></span>
              </div>
              <div class="address-secondary">
                {{ suggestion.secondary_text }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group col-md-3">
        <label for="radius">{{ $t("Radius (KM)") }}:</label>
        <input
            type="number"
            id="radius"
            v-model.number="filters.radius"
            class="form-control"
            min="1"
            max="500"
            step="1"
            :placeholder="addressCoordinates ? $t('Distance in kilometers') : $t('Enter an address first')"
            :disabled="!addressCoordinates"
            @input="handleRadiusChange"
        />
        <small v-if="!addressCoordinates" class="text-muted">
          <i class="fas fa-info-circle mr-1"></i>{{ $t("Please enter an address first") }}
        </small>
      </div>

      <div class="form-group col-md-3 d-flex align-items-end">
        <button
            type="button"
            class="btn btn-outline-secondary mr-2"
            @click="clearLocationFilters"
            :disabled="!filters.address && !filters.radius"
        >
          <i class="fas fa-times"></i> {{ $t("Clear") }}
        </button>

        <button
            type="button"
            class="btn btn-success"
            @click="calculateDistances"
            :disabled="!addressCoordinates || !filters.radius || loadingStates.distances"
        >
          <span v-if="loadingStates.distances" class="spinner-border spinner-border-sm mr-1"></span>
          <i v-else class="fas fa-calculator"></i>
          {{ $t("Calculate") }}
        </button>
      </div>

      <div class="col-12" v-if="distanceResults.length > 0">
        <div class="card shadow-sm mb-3">
          <!-- ✅ HEADER COM LEGENDA COMPACTA -->
          <div class="card-header bg-white border-bottom">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <h5 class="mb-0 text-primary mr-4">
                  <i class="fas fa-route mr-2"></i>{{ $t("Distance & Time Results") }}
                </h5>
                <div class="d-flex align-items-center traffic-legend">
                  <small class="text-muted mr-2">{{ $t("Transit") }}:</small>
                  <span class="badge badge-success badge-sm mr-1">
                    {{ $t("Light") }}
                  </span>
                  <span class="badge badge-warning badge-sm mr-1">
                    {{ $t("Moderate") }}
                  </span>
                  <span class="badge badge-danger badge-sm">
                    {{ $t("Heavy") }}
                  </span>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <span class="badge badge-success mr-2 ml-2">
                  {{ $t("Calculated") }}: {{ distanceResults.length }}
                </span>
                <span v-if="lastCalculatedAt" class="small text-muted">
                  {{ $t("Updated") }}: {{ formatTime(lastCalculatedAt) }}
                </span>
              </div>
            </div>
          </div>

          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="thead-light">
                <tr>
                  <th class="border-0">
                    <i class="fas fa-car text-muted mr-1"></i>{{ $t("Vehicle") }}
                  </th>
                  <th class="border-0">
                    <i class="fas fa-route text-muted mr-1"></i>{{ $t("Distance") }}
                  </th>
                  <th class="border-0">
                    <i class="fas fa-tachometer-alt text-muted mr-1"></i>{{ $t("Without Traffic") }}
                  </th>
                  <th class="border-0">
                    <i class="fas fa-traffic-light text-muted mr-1"></i>{{ $t("With Traffic") }}
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(result, index) in distanceResults"
                    :key="result.vehicle_id">
                  <td class="align-middle">
                    <div class="d-flex align-items-center">
                      <div class="vehicle-indicator mr-2"
                           :class="result.status === 'OK' ? 'status-success' : 'status-warning'"></div>
                      <div>
                        <strong class="text-dark">{{ result.vehicle_name }}</strong>
                        <small v-if="result.status === 'OK'" class="d-block text-muted">
                          #{{ index + 1 }} • {{ $t("Closest") }}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td class="align-middle">
                    <span v-if="result.status === 'OK'" class="badge badge-primary">
                      {{ result.distance_text }}
                    </span>
                    <span v-else class="badge badge-secondary">N/A</span>
                  </td>
                  <td class="align-middle">
                    <span v-if="result.status === 'OK'" class="badge badge-info">
                      {{ result.duration_text }}
                    </span>
                    <span v-else class="badge badge-secondary">N/A</span>
                  </td>
                  <td class="align-middle">
                    <span v-if="result.status === 'OK'" class="badge" :class="getTrafficBadgeClass(result)">
                      {{ result.duration_in_traffic_text }}
                    </span>
                    <span v-else class="badge badge-secondary">N/A</span>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <MapPositionNowPointsList
            ref="mapList"
            :zoomMapa="mapZoom"
            :vehicleFleet="allVehiclesForMap"
            :selectedVehicleId="filters.vehicle_id"
            :addressCoordinates="addressCoordinates"
            :radiusKm="filters.radius"
            @reload="loadVehicleFleet"
        />
      </div>
    </div>
  </div>
</template>

<script>
import MapPositionNowPointsList from "@/layouts/Mapa/MapPositionNowPointsList.vue";
import axios from "axios";
import env from "@/config/env.js";

export default {
  name: "Routes",
  components: {MapPositionNowPointsList},

  data() {
    return {
      filters: {
        center_id: null,
        unit_id: null,
        vehicle_id: null,
        address: '',
        radius: null,
        vehicle_type_code: null,
      },
      loadingStates: {
        centers: false,
        units: false,
        vehicles: false,
        fleet: false,
        geocoding: false,
        distances: false,
        vehicle_types: false,
      },
      centers: [],
      units: [],
      vehicles: [],
      vehicleFleet: [],
      vehicleTypes: [],
      addressCoordinates: null,
      mapZoom: 13,
      distanceResults: [],
      lastCalculatedAt: null,
      autocomplete: null,
      googleApiKey: env.VUE_APP_GOOGLE_MAPS_API_KEY,
      addressSuggestions: [],
      showAddressDropdown: false,
      selectedSuggestionIndex: -1,
      searchTimeout: null,
      placesService: null
      ,
      lastUpdateListener: null
    };
  },

  computed: {
    companyId() {
      return this.$store.state.user.company_id > 1
          ? this.$store.state.user.company_id
          : null;
    },

    centerId() {
      return this.$store.state.user.center_id > 1
          ? this.$store.state.user.center_id
          : null;
    },

    unitId() {
      return this.$store.state.user.unit_id > 1
          ? this.$store.state.user.unit_id
          : null;
    },

    centerOptions() {
      return [{id: null, name: "Todos"}, ...this.centers];
    },

    unitOptions() {
      return [{id: null, name: "Todos"}, ...this.units];
    },

    vehicleTypeOptions() {
      const list = (this.vehicleTypes || [])
          .filter(t => t && (Number(t.status) === 1 || t.status === true))
          .map(t => ({
            code: String(t.sigla || '').toUpperCase() || null,
            name: (t.sigla ? String(t.sigla).toUpperCase() : '') + (t.name ? ` - ${t.name}` : '')
          }))
          .filter(x => x.code);
      return [{code: null, name: "Todos"}, ...list];
    },

    vehicleOptions() {
      let availableVehicles = this.vehicles
          .map(vehicleData => vehicleData.vehicle)
          .filter(vehicle => vehicle && vehicle.status === 1);

      if (this.filters.center_id || this.filters.unit_id) {
        availableVehicles = availableVehicles.filter(vehicle =>
            this.filters.unit_id
                ? Number(vehicle.unit_id) === Number(this.filters.unit_id)
                : this.filters.center_id
                    ? Number(vehicle.center_id) === Number(this.filters.center_id)
                    : true
        );
      }

      const formattedVehicles = availableVehicles.map(vehicle => ({
        id: vehicle.id,
        name: vehicle.name,
      }));

      return [
        {id: null, name: "Todos"},
        ...new Map(formattedVehicles.map(vehicle => [vehicle.id, vehicle])).values()
      ];
    },

    allVehiclesForMap() {
      return this.vehicleFleet;
    }
  },

  methods: {
    getRecordVehicleId(record) {
      if (!record) return null;
      const fromVehicle = record.vehicle && record.vehicle.id != null ? Number(record.vehicle.id) : null;
      const fromVehicleId = record.vehicle_id != null ? Number(record.vehicle_id) : null;
      const fromId = record.id != null ? Number(record.id) : null;
      return [fromVehicle, fromVehicleId, fromId].find(v => v !== null && !Number.isNaN(v)) ?? null;
    },

    async initGooglePlaces() {
      try {
        await this.loadGooglePlacesAPI();
        if (window.google?.maps?.places) {
          this.placesService = new window.google.maps.places.AutocompleteService();
        }
      } catch (error) {
        console.error('Erro ao inicializar Google Places:', error);
      }
    },

    handleAddressInput() {
      this.addressCoordinates = null;
      this.distanceResults = [];

      if (this.searchTimeout) clearTimeout(this.searchTimeout);

      if (this.filters.address.length < 3) {
        this.addressSuggestions = [];
        this.showAddressDropdown = false;
        return;
      }

      this.searchTimeout = setTimeout(() => this.searchAddresses(), 300);
    },

    handleAddressFocus() {
      this.showAddressDropdown = this.addressSuggestions.length > 0;
    },

    handleAddressBlur() {
      setTimeout(() => {
        this.showAddressDropdown = false;
        this.selectedSuggestionIndex = -1;
      }, 200);
    },

    handleAddressKeydown(event) {
      if (!this.showAddressDropdown || this.addressSuggestions.length === 0) return;

      const actions = {
        'ArrowDown': () => {
          event.preventDefault();
          this.selectedSuggestionIndex = Math.min(
              this.selectedSuggestionIndex + 1,
              this.addressSuggestions.length - 1
          );
        },
        'ArrowUp': () => {
          event.preventDefault();
          this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, -1);
        },
        'Enter': () => {
          event.preventDefault();
          if (this.selectedSuggestionIndex >= 0) {
            this.selectAddressSuggestion(this.addressSuggestions[this.selectedSuggestionIndex]);
          }
        },
        'Escape': () => {
          this.showAddressDropdown = false;
          this.selectedSuggestionIndex = -1;
        }
      };

      actions[event.key]?.();
    },

    async searchAddresses() {
      if (!this.placesService || !this.filters.address || this.filters.address.length < 3) return;

      const request = {
        input: this.filters.address,
        componentRestrictions: {country: 'br'},
        types: ['address']
      };

      this.placesService.getPlacePredictions(request, (predictions, status) => {
        const isOk = status === window.google.maps.places.PlacesServiceStatus.OK;

        this.addressSuggestions = isOk && predictions
            ? predictions.map(prediction => ({
              place_id: prediction.place_id,
              main_text: prediction.structured_formatting.main_text,
              secondary_text: prediction.structured_formatting.secondary_text,
              description: prediction.description
            }))
            : [];

        this.showAddressDropdown = isOk && predictions;
      });
    },

    async selectAddressSuggestion(suggestion) {
      this.filters.address = suggestion.description;
      this.showAddressDropdown = false;
      this.selectedSuggestionIndex = -1;
      this.addressSuggestions = [];
      await this.geocodeAddress();
    },

    highlightMatch(text, search) {
      if (!search) return text;
      const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return text.replace(regex, '<strong class="text-primary">$1</strong>');
    },

    clearAddress() {
      Object.assign(this.filters, {address: '', radius: null});
      Object.assign(this, {
        addressCoordinates: null,
        distanceResults: [],
        addressSuggestions: [],
        showAddressDropdown: false
      });
      this.adjustMapZoom();
      this.$refs.addressInput.focus();
    },

    getTrafficBadgeClass(result) {
      if (result.status !== 'OK') return 'badge-secondary';

      const regularTime = result.duration_value;
      const trafficTime = result.duration_in_traffic_value;
      const ratio = trafficTime / regularTime;

      return ratio > 1.3 ? 'badge-danger'
          : ratio > 1.1 ? 'badge-warning'
              : 'badge-success';
    },

    loadGooglePlacesAPI() {
      return new Promise((resolve, reject) => {
        if (window.google?.maps?.places) {
          resolve();
          return;
        }

        if (document.querySelector('script[src*="maps.googleapis.com"]')) {
          const checkLoaded = setInterval(() => {
            if (window.google?.maps?.places) {
              clearInterval(checkLoaded);
              resolve();
            }
          }, 100);
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleApiKey}&libraries=places&callback=initGoogleMaps`;
        script.async = true;
        script.defer = true;

        window.initGoogleMaps = () => {
          delete window.initGoogleMaps;
          resolve();
        };

        script.onerror = (error) => {
          reject(new Error('Falha ao carregar Google Maps API'));
        };

        document.head.appendChild(script);
      });
    },

    getVehicleName: (vehicle) => vehicle.vehicle?.name || vehicle.name || `Veículo ${vehicle.vehicle_id || vehicle.id}`,

    formatTime(timeString) {
      return !timeString ? '' : new Date(timeString).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    calculateDistance(lat1, lng1, lat2, lng2) {
      const R = 6371;
      const dLat = this.toRadians(lat2 - lat1);
      const dLng = this.toRadians(lng2 - lng1);

      const a = Math.sin(dLat / 2) ** 2 +
          Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
          Math.sin(dLng / 2) ** 2;

      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

    toRadians: (degrees) => degrees * (Math.PI / 180),

    async handleAddressChange() {
      this.filters.address?.length > 5
          ? await this.geocodeAddress()
          : (this.addressCoordinates = null, this.distanceResults = []);
    },

    handleRadiusChange() {
      this.filters.radius = typeof this.filters.radius === 'string'
          ? parseFloat(this.filters.radius) || null
          : this.filters.radius;

      if (this.addressCoordinates && this.filters.radius) {
        this.adjustMapZoom();
        this.distanceResults = [];
      }
    },

    clearLocationFilters() {
      Object.assign(this.filters, {address: '', radius: null});
      Object.assign(this, {
        addressCoordinates: null,
        distanceResults: [],
        addressSuggestions: [],
        showAddressDropdown: false
      });
      this.adjustMapZoom();
    },

    async geocodeAddress() {
      if (!this.filters.address) return;

      this.loadingStates.geocoding = true;

      try {
        const response = await axios.post(`${this.$store.state.api}routes/geocode-address`, {
          address: this.filters.address
        });

        response.data.success
            ? (this.addressCoordinates = {
              lat: response.data.data.lat,
              lng: response.data.data.lng,
              formatted_address: response.data.data.formatted_address
            }, this.adjustMapZoom(), this.distanceResults = [])
            : (this.$message(null, response.data.message || 'Endereço não encontrado', 'warning'),
                this.addressCoordinates = null,
                this.filters.radius = null);
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Erro ao buscar endereço';
        this.$message(null, errorMessage, 'error');
        this.addressCoordinates = null;
        this.filters.radius = null;
      } finally {
        this.loadingStates.geocoding = false;
      }
    },

    async calculateDistances() {
      if (!this.addressCoordinates) {
        this.$message(null, this.$t('Please specify an address'), 'warning');
        return;
      }

      if (!this.filters.radius) {
        this.$message(null, this.$t('Please specify a radius in km to calculate distances'), 'warning');
        return;
      }

      this.loadingStates.distances = true;
      this.distanceResults = [];

      try {
        const vehiclesInRadius = this.vehicleFleet
            .filter(vehicle => {
              if (!vehicle.position?.lat || !vehicle.position?.lng) return false;

              const distance = this.calculateDistance(
                  this.addressCoordinates.lat,
                  this.addressCoordinates.lng,
                  vehicle.position.lat,
                  vehicle.position.lng
              );

              return distance <= this.filters.radius;
            });

        const vehiclesData = vehiclesInRadius.map(vehicle => ({
          id: vehicle.vehicle_id || vehicle.id,
          name: vehicle.vehicle?.name || vehicle.name || `Veículo ${vehicle.vehicle_id || vehicle.id}`,
          lat: vehicle.position.lat,
          lng: vehicle.position.lng
        }));

        if (vehiclesData.length === 0) {
          this.$message(null, this.$t('No vehicles found within the specified radius'), 'warning');
          return;
        }

        const payload = {
          origin_lat: this.addressCoordinates.lat,
          origin_lng: this.addressCoordinates.lng,
          vehicles: vehiclesData
        };

        const response = await axios.post(`${this.$store.state.api}routes/calculate-distances`, payload);

        response.data.success
            ? (this.distanceResults = response.data.data,
                this.lastCalculatedAt = response.data.calculated_at,
                this.$message(null, `Distâncias calculadas para ${vehiclesData.length} veículos: ${response.data.successful_calculations || 0} sucessos, ${response.data.failed_calculations || 0} falhas`, 'success'))
            : (() => {
              throw new Error('Resposta da API inválida');
            })();

      } catch (error) {
        this.$message(null, error.response?.data?.message || 'Erro ao calcular distâncias', 'error');
      } finally {
        this.loadingStates.distances = false;
      }
    },

    async handleCenterChange(centerId) {
      this.filters.unit_id = null;
      this.filters.vehicle_id = null;
      this.filters.vehicle_type_code = null; // reset type on center change
      this.distanceResults = [];

      centerId
          ? await this.loadUnits(centerId)
          : this.units = [];

      await Promise.all([this.loadVehicles(), this.loadVehicleFleet()]);
      this.reframeMap();
    },

    async handleUnitChange() {
      this.filters.vehicle_id = null;
      this.filters.vehicle_type_code = null; // reset type on unit change
      this.distanceResults = [];
      await Promise.all([this.loadVehicles(), this.loadVehicleFleet()]);
      this.reframeMap();
    },

    handleVehicleTypeChange() {
      this.filters.vehicle_id = null;
      this.distanceResults = [];
      this.loadVehicleFleet();
      this.reframeMap();
    },

    handleVehicleChange() {
      this.distanceResults = [];
      this.loadVehicleFleet();
      this.reframeMap();
    },

    async loadCenters() {
      this.loadingStates.centers = true;
      try {
        const api = this.companyId
            ? `${this.$store.state.api}companies/centers/${this.companyId}`
            : `${this.$store.state.api}centers/`;
        const response = await axios.get(api);
        this.centers = Array.isArray(response.data)
            ? response.data
            : (response.data.data || []);
      } catch (error) {
        this.$error(error);
      } finally {
        this.loadingStates.centers = false;
      }
    },

    async loadUnits(centerId) {
      this.loadingStates.units = true;
      try {
        const response = await axios.get(`${this.$store.state.api}centers/unit/${centerId}`);
        this.units = response.data || [];
      } catch (error) {
        this.$error(error);
      } finally {
        this.loadingStates.units = false;
      }
    },

    async loadVehicles() {
      this.loadingStates.vehicles = true;
      try {
        const params = {
          paginated: false,
          status: 1,
          ...(this.filters.vehicle_id && {vehicle_id: this.filters.vehicle_id}),
          ...(this.filters.center_id && {center_id: this.filters.center_id}),
          ...(this.filters.unit_id && {unit_id: this.filters.unit_id}),
          ...(this.filters.vehicle_type_code && {vehicle_type_code: this.filters.vehicle_type_code}),
        };

        const response = await axios.get(`${this.$store.state.api}vehicle_last_updates/filtered`, {params});
        this.vehicles = response.data.data || [];
      } catch (error) {
        // Silent error
      } finally {
        this.loadingStates.vehicles = false;
      }
    },

    async loadVehicleTypes() {
      this.loadingStates.vehicle_types = true;
      try {
        const resp = await axios.get(`${this.$store.state.api}vehicle_types`);
        const payload = resp && resp.data ? resp.data : [];
        const list = Array.isArray(payload) ? payload : (payload.data || []);
        this.vehicleTypes = Array.isArray(list) ? list : [];
      } catch (e) {
        this.$error(e);
      } finally {
        this.loadingStates.vehicle_types = false;
      }
    },

    async loadVehicleFleet() {
      this.loadingStates.fleet = true;
      try {
        const params = {
          paginated: false,
          status: 1,
          ...(this.filters.vehicle_id && {vehicle_id: this.filters.vehicle_id}),
          ...(this.filters.unit_id && {unit_id: this.filters.unit_id}),
          ...(this.filters.center_id && {center_id: this.filters.center_id}),
          ...(this.filters.vehicle_type_code && {vehicle_type_code: this.filters.vehicle_type_code}),
        };

        const response = await axios.get(`${this.$store.state.api}vehicle_last_updates/filtered`, {params});

        this.vehicleFleet = (response.data.data || [])
            .filter(vehicleData => vehicleData.vehicle && vehicleData.vehicle.status === 1)
            .map(vehicleData => ({
              ...vehicleData,
              position: vehicleData.lat && vehicleData.long ? {
                icon: vehicleData.vehicle && vehicleData.vehicle.vehicle_type
                    ? vehicleData.vehicle.vehicle_type.file
                    : null,
                lat: parseFloat(vehicleData.lat),
                lng: parseFloat(vehicleData.long),
                speed: vehicleData.speed,
                ignition_status: vehicleData.ignition_status,
                port_status: vehicleData.port_status,
                siren_status: vehicleData.siren_status,
                device_battery_level: vehicleData.device_battery_level,
              } : null,
            }));
      } catch (error) {
        this.$message(null, error.response?.data || error.message, "error");
      } finally {
        this.loadingStates.fleet = false;
      }
    },

    reframeMap() {
      if (this.$refs.mapList && this.$refs.mapList.fitAll) {
        this.$refs.mapList.fitAll();
      }
    },

    adjustMapZoom() {
      if (this.addressCoordinates && this.filters.radius) return;

      this.mapZoom = this.filters.vehicle_id ? 18
          : this.filters.vehicle_type_code ? 12
              : this.filters.unit_id ? 12
                  : this.filters.center_id ? 10
                      : 8;
    },

    handleWebSocketUpdate(vehicleUpdate) {
      if (!vehicleUpdate || vehicleUpdate.vehicle_id == null) return;
      if (!this.shouldUpdateVehicle(vehicleUpdate)) return;

      const updateId = Number(vehicleUpdate.vehicle_id);
      const vehicleIndex = this.vehicleFleet.findIndex(
          fleetItem => this.getRecordVehicleId(fleetItem) === updateId
      );
      const previousVehicle = vehicleIndex !== -1 ? this.vehicleFleet[vehicleIndex] : null;

      if (this.isStaleUpdate(vehicleUpdate, previousVehicle)) return;

      const updatedVehicleData = this.createUpdatedVehicle(vehicleUpdate, previousVehicle);

      if (vehicleIndex !== -1) {
        this.$set(this.vehicleFleet, vehicleIndex, {...this.vehicleFleet[vehicleIndex], ...updatedVehicleData});
      } else {
        if (this.filters.vehicle_type_code) {
          const allowedIds = new Set(this.vehicleFleet.map(item => this.getRecordVehicleId(item)).filter(id => id !== null));
          if (!allowedIds.has(updateId)) return;
        }
        this.vehicleFleet.unshift(updatedVehicleData);
      }
    },

    shouldUpdateVehicle(vehicleUpdate) {
      if (!vehicleUpdate || vehicleUpdate.vehicle_id == null) return false;
      const allowedIds = new Set(
          (this.vehicles || [])
              .map(item => this.getRecordVehicleId(item))
              .filter(id => id !== null)
      );

      if (!allowedIds.has(Number(vehicleUpdate.vehicle_id))) {
        return false;
      }

      const mismatch = (
          (this.filters.vehicle_id && Number(vehicleUpdate.vehicle_id) !== Number(this.filters.vehicle_id)) ||
          (this.filters.unit_id && Number(vehicleUpdate.unit_id) !== Number(this.filters.unit_id)) ||
          (this.filters.center_id && Number(vehicleUpdate.center_id) !== Number(this.filters.center_id))
      );
      if (mismatch) return false;

      if (this.filters.vehicle_type_code) {
        const allowedIds = new Set(this.vehicleFleet.map(item => this.getRecordVehicleId(item)).filter(id => id !== null));
        if (!allowedIds.has(Number(vehicleUpdate.vehicle_id))) return false;
      }

      return true;
    },

    isStaleUpdate(vehicleUpdate, currentVehicle) {
      const incoming = this.trackingTime(vehicleUpdate);
      const current = this.trackingTime(currentVehicle);

      if (incoming === null || current === null) return false;

      return incoming < current;
    },

    trackingTime(record) {
      const raw = record ? record.last_tracking_at : null;

      if (!raw) return null;

      const parsed = Date.parse(String(raw).replace(" ", "T"));

      return Number.isNaN(parsed) ? null : parsed;
    },

    createUpdatedVehicle(vehicleData, previousVehicle = null) {
      const iconFromPayload = vehicleData.vehicle && vehicleData.vehicle.vehicle_type
          ? vehicleData.vehicle.vehicle_type.file
          : null;
      const iconFromPrevious = previousVehicle && previousVehicle.position
          ? previousVehicle.position.icon
          : null;

      return {
        ...vehicleData,
        position: vehicleData.lat && vehicleData.long ? {
          icon: iconFromPayload || iconFromPrevious || null,
          lat: parseFloat(vehicleData.lat),
          lng: parseFloat(vehicleData.long),
          speed: vehicleData.speed,
          ignition_status: vehicleData.ignition_status,
          port_status: vehicleData.port_status,
          siren_status: vehicleData.siren_status,
          device_battery_level: vehicleData.device_battery_level,
        } : (previousVehicle ? previousVehicle.position : null),
      };
    },

    async applyUrlAddressIfPresent() {
      try {
        const endereco = this.$route?.query?.endereco ||
            new URLSearchParams(window.location.search).get('endereco');

        if (!endereco) return;

        const cleanAddress = decodeURIComponent(String(endereco).replace(/\+/g, ' ')).trim();
        if (!cleanAddress) return;

        this.filters.address = cleanAddress;
        this.filters.radius = this.filters.radius || 10;

        await Promise.all([this.loadVehicles(), this.loadVehicleFleet()]);
        await this.geocodeAddress();

        this.addressCoordinates && await this.calculateDistances();
      } catch (err) {
        console.error('Erro aplicando endereco da URL:', err);
      }
    },
  },

  async mounted() {
    if (this.centerId) {
      this.filters.center_id = this.centerId;
    }

    if (this.unitId) {
      this.filters.unit_id = this.unitId;
    }

    await this.loadCenters();
    await this.loadVehicleTypes();

    if (this.filters.center_id) {
      await this.loadUnits(this.filters.center_id);
    }

    await this.loadVehicles();
    await this.loadVehicleFleet();

    this.$nextTick(() => {
      this.initGooglePlaces();
      this.applyUrlAddressIfPresent();
    });

    this.lastUpdateListener = (payload) => {
      const data = Array.isArray(payload) ? payload[0] : payload;
      this.handleWebSocketUpdate(data);
    };

    window.Echo.channel("last-update").listen(".last-update", this.lastUpdateListener);
  },

  watch: {
    '$route.query.endereco': function (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.applyUrlAddressIfPresent();
      }
    }
  },

  beforeDestroy() {
    if (window.initGoogleMaps) {
      delete window.initGoogleMaps;
    }

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (window.Echo && window.Echo.channel) {
      try {
        window.Echo.channel("last-update").stopListening(".last-update", this.lastUpdateListener);
      } catch (e) {
        // no-op
      }
    }

    this.lastUpdateListener = null;
  }
};
</script>


<style scoped>
.vselect {
  width: 100%;
}

::v-deep .vs__spinner {
  border-width: 2px;
}

.table-responsive {
  max-height: 500px;
  overflow-y: auto;
}

.badge {
  font-size: 0.875rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.card {
  border: none;
  border-radius: 12px;
}

.card-header {
  border-radius: 12px 12px 0 0 !important;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.table {
  border-radius: 0 0 12px 12px;
}

.table th {
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
  background-color: #f8f9fa;
}

.table td {
  vertical-align: middle;
  border-top: 1px solid #f1f3f4;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.vehicle-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-success {
  background-color: #28a745;
}

.status-warning {
  background-color: #ffc107;
}

.table tbody tr {
  transition: background-color 0.2s ease;
}

.badge {
  transition: all 0.2s ease;
}

.table-success, .table-warning, .table-info {
  background-color: transparent !important;
}

.badge-sm {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.traffic-legend {
  background: rgba(248, 249, 250, 0.8);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 1px solid #e9ecef;
}

input:disabled {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  cursor: not-allowed !important;
  border-color: #e9ecef !important;
}

input:disabled::placeholder {
  color: #adb5bd !important;
  font-style: italic;
}

.custom-address-input {
  position: relative;
  width: 100%;
}

.address-input-container {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.address-input-container:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.address-icon {
  position: absolute;
  left: 12px;
  color: #6c757d;
  font-size: 16px;
  z-index: 2;
}

.address-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 16px 12px 40px;
  font-size: 14px;
  border-radius: 6px;
  background: transparent;
}

.address-input::placeholder {
  color: #adb5bd;
}

.clear-address-btn {
  position: absolute;
  right: 40px;
  background: none;
  border: none;
  color: #6c757d;
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 2;
}

.clear-address-btn:hover {
  background-color: #f8f9fa;
  color: #dc3545;
}

.address-spinner {
  position: absolute;
  right: 12px;
  color: #007bff;
  z-index: 2;
}

.address-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
}

.address-option {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  transition: background-color 0.2s ease;
}

.address-option:last-child {
  border-bottom: none;
}

.address-option:hover,
.address-option.active {
  background-color: #f8f9fa;
}

.address-main {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #212529;
  margin-bottom: 4px;
}

.address-secondary {
  font-size: 13px;
  color: #6c757d;
  margin-left: 24px;
}

.position-relative {
  position: relative;
}
</style>
